import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, ArrowLeft } from 'lucide-react';
import { LocationSelector } from '@/components/LocationSelector';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function EditFamily() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    fetchFamily();
  }, [id]);

  const fetchFamily = async () => {
    try {
      const response = await api.get(`/families/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Failed to fetch family data');
      navigate('/families');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const submitData = {
        parent_first_name: formData.parent_first_name,
        parent_last_name: formData.parent_last_name,
        parent_role: formData.parent_role,
        email: formData.email,
        phone: formData.phone,
        location_type: formData.location_type,
        location_detail: formData.location_detail,
        ward_number: formData.ward_number,
        city_county: formData.city_county,
        virginia_city: formData.virginia_city,
        virginia_county: formData.virginia_county,
        maryland_city: formData.maryland_city,
        maryland_county: formData.maryland_county,
        address: formData.address,
        children: formData.children.map(child => ({
          ...child,
          age: parseInt(child.age) || 0
        })),
        already_has_bookshelf: formData.already_has_bookshelf,
        books_from_birth_signup: formData.books_from_birth_signup,
        books_count: formData.books_count,
        annual_income: formData.annual_income,
        photo_consent: formData.photo_consent,
        referral_source: formData.referral_source,
        user_id: formData.user_id
      };
      
      await api.put(`/families/${id}`, submitData);
      toast.success('Family updated successfully!');
      navigate('/families');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update family');
    } finally {
      setLoading(false);
    }
  };

  const addChild = () => {
    setFormData({
      ...formData,
      children: [...formData.children, {
        name: '',
        age: '',
        gender: 'boy',
        learning_stage: 'Early Learning',
        reading_level: 'Developing',
        favorite_interests: '',
        has_library_card: false
      }]
    });
  };

  const removeChild = (index) => {
    setFormData({
      ...formData,
      children: formData.children.filter((_, i) => i !== index)
    });
  };

  const updateChild = (index, field, value) => {
    const newChildren = [...formData.children];
    newChildren[index] = { ...newChildren[index], [field]: value };
    setFormData({ ...formData, children: newChildren });
  };

  if (fetching) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="ml-64 flex-1 p-8">
          <div className="text-center py-12">Loading...</div>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="edit-family-page">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/families')}
              className="mb-4"
              data-testid="back-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Families
            </Button>
            <h1 className="text-4xl font-bold text-foreground mb-2">Edit Family Profile</h1>
            <p className="text-stone-600">Update family information</p>
          </div>

          <form onSubmit={handleSubmit}>
            <Card className="border-stone-200 shadow-card mb-6">
              <CardHeader>
                <CardTitle>Parent Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="parent_first_name">First Name *</Label>
                    <Input
                      id="parent_first_name"
                      data-testid="parent-first-name"
                      value={formData.parent_first_name}
                      onChange={(e) => setFormData({ ...formData, parent_first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="parent_last_name">Last Name *</Label>
                    <Input
                      id="parent_last_name"
                      data-testid="parent-last-name"
                      value={formData.parent_last_name}
                      onChange={(e) => setFormData({ ...formData, parent_last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="parent_role">Parent/Guardian Role</Label>
                  <select
                    id="parent_role"
                    data-testid="parent-role"
                    value={formData.parent_role || ''}
                    onChange={(e) => setFormData({ ...formData, parent_role: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select role</option>
                    <option value="Mother">Mother</option>
                    <option value="Father">Father</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      data-testid="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      data-testid="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-card mb-6">
              <CardHeader>
                <CardTitle>Location Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <LocationSelector formData={formData} setFormData={setFormData} required={true} />
                
                <div>
                  <Label htmlFor="address">Full Address *</Label>
                  <Input
                    id="address"
                    data-testid="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Street address, city, state, zip"
                    required
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-card mb-6">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Children Information</CardTitle>
                  <Button type="button" variant="outline" onClick={addChild} data-testid="add-child-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {formData.children.map((child, index) => (
                  <div key={index} className="p-4 border border-stone-200 rounded-lg space-y-4" data-testid={`child-${index}`}>
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="font-medium">Child {index + 1}</h4>
                      {formData.children.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChild(index)}
                          data-testid={`remove-child-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          data-testid={`child-name-${index}`}
                          value={child.name}
                          onChange={(e) => updateChild(index, 'name', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Age *</Label>
                        <Input
                          data-testid={`child-age-${index}`}
                          type="number"
                          min="0"
                          max="18"
                          value={child.age}
                          onChange={(e) => updateChild(index, 'age', e.target.value)}
                          required
                        />
                      </div>
                      <div>
                        <Label>Gender *</Label>
                        <select
                          data-testid={`child-gender-${index}`}
                          value={child.gender}
                          onChange={(e) => updateChild(index, 'gender', e.target.value)}
                          className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="boy">Boy</option>
                          <option value="girl">Girl</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Learning Stage *</Label>
                        <select
                          data-testid={`child-learning-stage-${index}`}
                          value={child.learning_stage}
                          onChange={(e) => updateChild(index, 'learning_stage', e.target.value)}
                          className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="Early Learning">Early Learning</option>
                          <option value="Kindergarten">Kindergarten</option>
                          <option value="1st Grade">1st Grade</option>
                          <option value="2nd Grade">2nd Grade</option>
                          <option value="3rd Grade">3rd Grade</option>
                          <option value="Not in School">Not in School</option>
                          <option value="Homeschooling">Homeschooling</option>
                        </select>
                      </div>
                      <div>
                        <Label>Reading Level *</Label>
                        <select
                          data-testid={`child-reading-level-${index}`}
                          value={child.reading_level}
                          onChange={(e) => updateChild(index, 'reading_level', e.target.value)}
                          className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                        >
                          <option value="Developing">Developing</option>
                          <option value="Grade Level">Grade Level</option>
                          <option value="Advanced">Advanced</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Favorite Books, TV Characters, Superheroes, or Interests</Label>
                      <Input
                        data-testid={`child-interests-${index}`}
                        value={child.favorite_interests || ''}
                        onChange={(e) => updateChild(index, 'favorite_interests', e.target.value)}
                        placeholder="e.g., Harry Potter, Spider-Man, dinosaurs"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id={`library-card-${index}`}
                        data-testid={`child-library-card-${index}`}
                        checked={child.has_library_card}
                        onCheckedChange={(checked) => updateChild(index, 'has_library_card', checked)}
                      />
                      <Label htmlFor={`library-card-${index}`} className="cursor-pointer">
                        Child has a library card
                      </Label>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border-stone-200 shadow-card mb-6">
              <CardHeader>
                <CardTitle>Household Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="books_count">Number of Children's Books in Home *</Label>
                    <select
                      id="books_count"
                      data-testid="books-count"
                      value={formData.books_count}
                      onChange={(e) => setFormData({ ...formData, books_count: e.target.value })}
                      className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    >
                      <option value="None">None</option>
                      <option value="1-10">1-10</option>
                      <option value="11-20">11-20</option>
                      <option value="More than 20">More than 20</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="annual_income">Annual Household Income *</Label>
                    <Input
                      id="annual_income"
                      data-testid="annual-income"
                      value={formData.annual_income}
                      onChange={(e) => setFormData({ ...formData, annual_income: e.target.value })}
                      placeholder="e.g., $0-$25,000"
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="referral_source">How did you learn about The Bookshelf Project?</Label>
                  <select
                    id="referral_source"
                    data-testid="referral-source"
                    value={formData.referral_source || ''}
                    onChange={(e) => setFormData({ ...formData, referral_source: e.target.value })}
                    className="w-full h-11 px-4 rounded-lg border border-stone-200 focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select an option</option>
                    <option value="School">School</option>
                    <option value="Community Event">Community Event</option>
                    <option value="Friend">Friend</option>
                    <option value="Online">Online</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="already_has_bookshelf"
                      data-testid="has-bookshelf"
                      checked={formData.already_has_bookshelf}
                      onCheckedChange={(checked) => setFormData({ ...formData, already_has_bookshelf: checked })}
                    />
                    <Label htmlFor="already_has_bookshelf" className="cursor-pointer">
                      Family already has a bookshelf
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="books_from_birth_signup"
                      data-testid="books-from-birth"
                      checked={formData.books_from_birth_signup}
                      onCheckedChange={(checked) => setFormData({ ...formData, books_from_birth_signup: checked })}
                    />
                    <Label htmlFor="books_from_birth_signup" className="cursor-pointer">
                      Child is signed up with Books from Birth (DC Public Library)
                    </Label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="photo_consent"
                      data-testid="photo-consent"
                      checked={formData.photo_consent}
                      onCheckedChange={(checked) => setFormData({ ...formData, photo_consent: checked })}
                    />
                    <Label htmlFor="photo_consent" className="cursor-pointer">
                      Consent to video and photos for promotional purposes
                    </Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/families')}
                className="flex-1"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 rounded-full"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? 'Updating...' : 'Update Family'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}